/**
 * Resolve / prefetch Content Inventory audio for Quiz playback.
 * Prefers Dexie blob cache; falls back to remote audioUrl or short-lived sign.
 */

import type { Question } from '@examflow/core';
import { bankAudioCache } from '../../data/local/bankAudioCache';
import { banks as banksApi } from '../../lib/api';

const objectUrlByKey = new Map<string, string>();
const inflight = new Map<string, Promise<string | undefined>>();

export function buildAudioCacheKey(
  question: Pick<Question, 'id' | 'audioObjectName' | 'audioUrl'>
): string {
  if (question.audioObjectName?.trim()) {
    return `${question.id}:obj:${question.audioObjectName.trim()}`;
  }
  if (question.audioUrl?.trim()) {
    return `${question.id}:url:${question.audioUrl.trim()}`;
  }
  return `${question.id}:`;
}

async function objectUrlForKey(key: string, blob: Blob): Promise<string> {
  const existing = objectUrlByKey.get(key);
  if (existing) return existing;
  const url = URL.createObjectURL(blob);
  objectUrlByKey.set(key, url);
  return url;
}

export async function resolveQuestionAudio(question: Question): Promise<string | undefined> {
  if (!question.audioUrl && !question.audioObjectName) return undefined;
  const key = buildAudioCacheKey(question);
  const cached = await bankAudioCache.get(key);
  if (cached) return objectUrlForKey(key, cached.blob);

  const pending = inflight.get(key);
  if (pending) return pending;

  const job = (async () => {
    let url = question.audioUrl?.trim();
    if ((!url || url.startsWith('blob:')) && question.audioObjectName) {
      url = (await banksApi.signAudio(question.audioObjectName)).url;
    }
    if (!url) return undefined;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`音频下载失败：${response.status}`);
      const blob = await response.blob();
      await bankAudioCache.put(key, blob);
      return objectUrlForKey(key, blob);
    } catch (error) {
      if (question.audioObjectName) {
        const signed = await banksApi.signAudio(question.audioObjectName);
        const retry = await fetch(signed.url);
        if (retry.ok) {
          const blob = await retry.blob();
          await bankAudioCache.put(key, blob);
          return objectUrlForKey(key, blob);
        }
      }
      // Fall back to remote URL so playback still works without local cache.
      return url;
    }
  })().finally(() => {
    inflight.delete(key);
  });

  inflight.set(key, job);
  return job;
}

/** Cache hit only — never hits the network. */
export async function applyCachedPlayableAudio(questions: Question[]): Promise<Question[]> {
  return Promise.all(
    questions.map(async (q) => {
      if (!q.audioUrl && !q.audioObjectName) return q;
      const key = buildAudioCacheKey(q);
      const cached = await bankAudioCache.get(key);
      if (!cached) return q;
      const playable = await objectUrlForKey(key, cached.blob);
      return { ...q, audioUrl: playable };
    })
  );
}

/** Background prefetch for the current paper; does not block UI. */
export function prefetchQuestionsAudio(questions: Question[], concurrency = 3): void {
  const queue = questions.filter((q) => q.audioUrl || q.audioObjectName);
  let index = 0;
  const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
    while (index < queue.length) {
      const current = queue[index++];
      if (!current) break;
      try {
        await resolveQuestionAudio(current);
      } catch {
        // ignore prefetch errors
      }
    }
  });
  void Promise.all(workers);
}
